import { ApiResponse, apiClient } from '@api/apiClient';
import { ToastType } from '@components/Toast';
import { ILoadBoardViewModel } from '@store/loadboard';
import { userStore } from '@store/user';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast, printError } from '@utils/utils';
import { resetDefaults } from '@views/carrierMatching/helpers';
import { TruckT, defaultsT, editTruckT } from '@views/carrierMatching/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { carrierDefaultsT } from '@typeDefinitions/capacityTypes';

import {
  carrierCapStore,
  drawerStore,
  setDefaultStore,
  setDrawerStore,
} from './store';
import {
  AllBidsOfLoadResponseT,
  BidResponseT,
  CapacitySidepanelModelT,
  ConsistentTruck,
  EquipmentT,
  OfferT,
  SideCardBidT,
  TruckTransportT,
  initialCarrierDefaults,
} from './types';
import { truckSorter } from './utils';

const normalizeCarrierDefaults = (
  defaults: Omit<
    carrierDefaultsT,
    'defaultOriginCountry' | 'defaultDestinationCountry'
  > & {
    defaultOriginCountry: string | null;
    defaultDestinationCountry: string | null;
  },
): carrierDefaultsT => {
  return {
    ...defaults,
    defaultOriginCountry:
      defaults.defaultOriginCountry?.trim() ||
      initialCarrierDefaults.defaultOriginCountry,
    defaultDestinationCountry:
      defaults.defaultDestinationCountry?.trim() ||
      initialCarrierDefaults.defaultDestinationCountry,
  };
};

export async function fetchCarriers(
  userId: number | undefined = undefined,
): Promise<CapacitySidepanelModelT> {
  try {
    let action: Promise<ApiResponse<CapacitySidepanelModelT>>;
    if (Boolean(userId)) {
      action = apiClient.get<ApiResponse<CapacitySidepanelModelT>>(
        `/capacity/sidepanel?userId=${userId}`,
      ) as unknown as Promise<ApiResponse<CapacitySidepanelModelT>>;
    } else {
      action = apiClient.get<ApiResponse<CapacitySidepanelModelT>>(
        '/capacity/sidepanel',
      ) as unknown as Promise<ApiResponse<CapacitySidepanelModelT>>;
    }

    const resp = await action;

    if (resp.isSuccess && 'value' in resp) {
      return resp.value;
    }

    return {
      owned: [],
      developing: [],
      recent: [],
      isCapacityTeamEnabled: false,
    };
  } catch (e) {
    printError('fetchCarriers', e);

    return {
      owned: [],
      developing: [],
      recent: [],
      isCapacityTeamEnabled: false,
    };
  }
}

export const carrierAdd = async (carrierId: number) => {
  try {
    await apiClient.post(`/capacity/carrier/add/${carrierId}`);
    return true;
  } catch (e) {
    printError('carrierAdd', e);

    return false;
  }
};

export const bidsByLoadId = async (loadId: number) => {
  try {
    const bidPayload: { value: AllBidsOfLoadResponseT } = await apiClient.get(
      `/capacity/truck/bid/load/allbids/${loadId}`,
    );
    return bidPayload.value;
  } catch (e) {
    printError('bidsByLoadId', e);
  }
};

//Intentionally no toast messages here
export const createConsistentTruck = async (
  truck: TruckT,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: { value: ConsistentTruck } = await apiClient.post(
      '/capacity/consistent-truck',
      truck,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return res.value.id;
  } catch (e) {
    logError('createConsistentTruck', e as Error);
  }
};

export const createFromConsistentTruck = async (
  consistentTruckId: number,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<TruckTransportT | undefined> => {
  try {
    const res = (await apiClient.post(
      `/capacity/consistent-truck/${consistentTruckId}/create-truck`,
      undefined,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    )) as unknown as ApiResponse<TruckTransportT>;
    handleToast(ToastType.Success, 'Successfully created truck ');
    return res.value;
  } catch (e) {
    handleToast(ToastType.Error, 'Error creating truck');
  }
};

export const createTruck = async (
  truck: TruckT,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<TruckTransportT | undefined> => {
  try {
    const res = (await apiClient.post(
      '/capacity/truck',
      {
        ...truck,
      },
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    )) as unknown as ApiResponse<TruckTransportT>;
    if (res.isSuccess) {
      handleToast(ToastType.Success, 'Successfully created truck');
      return res.value;
    }
    return undefined;
  } catch (e) {
    logError('createTruck', e as Error);
    handleToast(ToastType.Error, 'Error creating truck');
    return undefined;
  }
};

export const getEquipment = async () => {
  try {
    const res = (await apiClient.get('/equipment')) as unknown as ApiResponse<
      EquipmentT[]
    >;

    if (res.isSuccess) {
      return res.value;
    }

    return [];
  } catch (e) {
    logError('getEquipment', e as Error);

    return [];
  }
};

export const getOfferManager = async () => {
  try {
    const res = (await apiClient.get(
      '/capacity/offermanager',
    )) as unknown as ApiResponse<OfferT[]>;

    return res.value;
  } catch (e) {
    logError('getOfferManager', e as Error);
    throw createError(e, 'Error getting offer manager');
  }
};

export const getMyOffers = async () => {
  try {
    const res = (await apiClient.get(
      '/capacity/myoffers',
    )) as unknown as ApiResponse<OfferT[]>;

    return res.value;
  } catch (e) {
    logError('getMyOffers', e as Error);
    throw createError(e, 'Error getting offers');
  }
};

export const approveBid = async (id: number) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${id}/approve`,
    )) as unknown as ApiResponse<boolean>;

    return res.isSuccess && res.value;
  } catch (e) {
    logError('approveBid', e as Error);
    throw createError(e, 'Error approving bid');
  }
};

export const rejectBid = async (id: number) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${id}/reject`,
    )) as unknown as ApiResponse<boolean>;

    return res.isSuccess && res.value;
  } catch (e) {
    logError('rejectBid', e as Error);
    throw createError(e, 'Error rejecting bid');
  }
};

export const counterBid = async (id: number, amount: number) => {
  try {
    const res = (await apiClient.put(
      `/capacity/truck/bid/${id}/counter`,
      amount,
    )) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError('counterBid', e as Error);
    throw createError(e, 'Error countering bid');
  }
};

export const editBidAmount = async (id: number, amount: number) => {
  try {
    const res = (await apiClient.put(
      `/capacity/truck/bid/${id}/edit`,
      amount,
    )) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError('editBidAmount', e as Error);
    throw createError(e, 'Error editing bid amount');
  }
};

export const deleteBid = async (
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${id}/cancel`,
      undefined,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    )) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError('deleteBid', e as Error);
    throw createError(e, 'Error deleting bid');
  }
};

export const approveCounter = async (id: number) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${id}/approvecounter`,
    )) as unknown as ApiResponse<boolean>;

    return res.isSuccess && res.value;
  } catch (e) {
    logError('approveCounter', e as Error);
    throw createError(e, 'Error approving counter');
  }
};

export const getTrucksByCarrierId = async (
  id: number,
): Promise<TruckTransportT[]> => {
  try {
    const res = (await apiClient.get(
      `/capacity/trucks/carrier/${id}`,
    )) as unknown as ApiResponse<TruckTransportT[]>;

    if (res.isSuccess) {
      //sort by capacity status and also by created date
      return res.value.sort(truckSorter);
    }

    return [];
  } catch (e) {
    logError(`getTrucksByCarrierId(${id})`, e as Error);

    return [];
  }
};

export const getCarrierDefaults = async (carrierId: number) => {
  try {
    const res: { value: carrierDefaultsT; isSuccess: boolean } =
      await apiClient.get(`/capacity/metadata/carrier/${carrierId}`);
    if (res.isSuccess) {
      if (!Boolean(res.value)) {
        resetDefaults();
      } else {
        setDefaultStore(normalizeCarrierDefaults(res.value));
      }
    }
  } catch (e) {
    logError('getCarrierDefaults', e as Error);
    handleToast(ToastType.Error, 'Failed to fetch Carrier Defaults');
    return null;
  }
};

export const fetchCarrierOwner = async (
  carrierId: number,
): Promise<{ id: number; name: string | null } | null> => {
  try {
    const res: { value: carrierDefaultsT | null; isSuccess: boolean } =
      await apiClient.get(`/capacity/metadata/carrier/${carrierId}`);

    if (
      !res.isSuccess ||
      !res.value?.carrierOwnerUserId ||
      !res.value.carrierOwnerUserName
    ) {
      return null;
    }

    return {
      id: res.value.carrierOwnerUserId,
      name: res.value.carrierOwnerUserName,
    };
  } catch (e) {
    logError(`fetchCarrierOwner(${carrierId})`, e as Error);
    return null;
  }
};

export const updateCarrierMetadata = async (defaults: defaultsT) => {
  try {
    const res: { value: carrierDefaultsT; isSuccess: boolean } =
      await apiClient.put('/capacity/metadata/edit', defaults);
    if (res.isSuccess) {
      handleToast(ToastType.Success, 'Updated Carrier Defaults');
      setDefaultStore(normalizeCarrierDefaults(res.value));
      return res.value;
    }
  } catch (e) {
    logError('updateCarrierMetadata', e as Error);
    handleToast(ToastType.Error, 'Failed to update Carrier Defaults');
    return null;
  }
};

export const getCapacityTruckMatches = async (
  truckId: number,
): Promise<ILoadBoardViewModel[]> => {
  try {
    const res = (await apiClient.get(
      `/Capacity/truck/matches/${truckId}`,
    )) as unknown as ApiResponse<ILoadBoardViewModel[]>;

    if (res.isSuccess) {
      return res.value;
    }

    return [];
  } catch (e) {
    logError(`getCapacityTruckMatches(${truckId})`, e as Error);

    return [];
  }
};

export const declineMatchForTruckByLoad = async (
  truckId: number,
  loadId: number,
) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/${truckId}/declinematchbyload/${loadId}`,
    )) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError(`declineMatchByLoad(${truckId}, ${loadId})`, e as Error);

    return false;
  }
};

export const editTruck = async (truck: editTruckT) => {
  try {
    const res = (await apiClient.put(
      '/capacity/truck/edit',
      truck,
    )) as unknown as ApiResponse<TruckTransportT>;

    return res.value;
  } catch (error) {
    logError(`editTruck(${JSON.stringify(truck)})`, error as Error);
  }
};

export const matchTruck = async (truckId: number) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/match/${truckId}`,
    )) as unknown as ApiResponse<ILoadBoardViewModel[]>;
    if (Boolean(res.value)) {
      handleToast(ToastType.Success, 'Truck Re-Matched');
      return res.value;
    }
  } catch (error) {
    logError(`matchTruck(${truckId})`, error as Error);

    return [] as ILoadBoardViewModel[];
  }
};

export const removeRecentCarrier = async (
  carrierId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    await apiClient.delete(
      `/capacity/carrier/remove/${carrierId}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    handleToast(ToastType.Success, 'Carrier removed from recent list');
  } catch (error) {
    handleToast(ToastType.Error, 'Failed to remove Carrier');
  }
};

export const removeDevelopingCarrier = async (
  carrierId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    await apiClient.delete(
      `/capacity/carrier/remove/relation/${carrierId}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    handleToast(ToastType.Success, 'Carrier removed from unowned list');
  } catch (error) {
    logError('removeRecentCarrier', error as Error);
    handleToast(ToastType.Error, 'Failed to remove Carrier');
  }
};

export const removeOwnedCarrier = async (
  carrierId: number,
  userId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    await apiClient.delete(
      `/capacity/manager/remove/ownedcarrier/${carrierId}/${userId}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );

    return true;
  } catch (error) {
    logError('removeOwnedCarrier', error as Error);
    return false;
  }
};

export const bookLoad = async (bidId: number) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${bidId}/book`,
    )) as unknown as ApiResponse<boolean>;

    return res.isSuccess && res.value;
  } catch (error) {
    logError('bookLoad', error as Error);
    throw createError(error, 'Failed to book load');
  }
};

export const addDevelopingCarrier = async (carrierId: number) => {
  try {
    await apiClient.post(`/capacity/carrier/add/relation/${carrierId}`);
    handleToast(ToastType.Success, 'Carrier added to unowned list');
  } catch (error) {
    logError('addDevelopingCarrier', error as Error);
    handleToast(ToastType.Error, 'Failed to add Carrier');
  }
};

export const postBid = async (amount: number) => {
  try {
    const res: { value: SideCardBidT | null } = await apiClient.post(
      '/capacity/truck/bid/create',
      {
        carrierId: carrierCapStore.id,
        loadId: drawerStore.loadId,
        truckId: drawerStore.truckId,
        amount,
      },
    );
    if (res.value) {
      res.value.requestedBy = userStore.user.name;
    }
    return res.value;
  } catch (error) {
    logError('postBid', error as Error);
    throw createError(error, 'Bid failed to Submit');
  }
};

export const editBid = async (amount: number) => {
  try {
    const res: { value: SideCardBidT | null } = await apiClient.put(
      '/capacity/truck/bid/edit',
      {
        carrierId: carrierCapStore.id,
        loadId: drawerStore.loadId,
        truckId: drawerStore.truckId,
        id: drawerStore.yourBid?.id,
        amount,
      },
    );
    if (res.value) {
      res.value.requestedBy = userStore.user.name;
    }
    handleToast(ToastType.Success, 'Bid Re-Submitted');
    setDrawerStore('yourBid', res.value);

    if (!!res.value) {
      const idx = drawerStore.bids.findIndex(
        (b) => b.id === (res.value as SideCardBidT).id,
      );
      setDrawerStore('bids', idx, res.value);
    }

    return true;
  } catch (error) {
    logError('editBid', error as Error);
    handleToast(ToastType.Error, 'Bid failed to Re-Submit');
    return false;
  }
};

export const bidEmailRequest = async (loadIds: number[]) => {
  try {
    const res = (await apiClient.post(
      '/capacity/bid/email/request',
      loadIds,
    )) as unknown as BidResponseT;

    return res;
  } catch (error) {
    logError(`bidEmailRequest(${JSON.stringify(loadIds)})`, error as Error);

    return undefined;
  }
};

export const reactToBidById = async (
  bidId: number,
  action: 'approve' | 'reject',
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res = (await apiClient.post(
      `/capacity/truck/bid/${bidId}/${action}`,
      undefined,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    )) as unknown as ApiResponse<boolean>;
    return res.isSuccess && res.value;
  } catch (error) {
    logError(`reactToBid(${bidId}, '${action}')`, error as Error);

    return false;
  }
};

export const deleteTruckById = async (truckId: number) => {
  try {
    const res = (await apiClient.delete(
      `/capacity/truck/${truckId}`,
    )) as unknown as ApiResponse<boolean>;
    return res.isSuccess && res.value;
  } catch (error) {
    logError(`deleteTruckById(${truckId})`, error as Error);

    return false;
  }
};

export const rematchBatch = async (truckIds: number[], carrierId: number) => {
  try {
    const res = (await apiClient.post('/capacity/truck/match/batch', {
      carrierId,
      truckIds,
    })) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError(
      `rematchBatch(${JSON.stringify(truckIds)}, ${carrierId})`,
      e as Error,
    );
    throw createError(e, 'Bulk rematch failed');

    return false;
  }
};

export const deleteAllTrucks = async (truckIds: number[]) => {
  try {
    const res = (await apiClient.delete('/capacity/deletealltruck', {
      data: truckIds,
    })) as unknown as ApiResponse<unknown>;

    return res.isSuccess;
  } catch (e) {
    logError(`deleteAllTrucks(${JSON.stringify(truckIds)})`, e as Error);
    throw createError(e, 'Bulk truck delete failed');
  }
};
